import { useSuspenseQuery } from '@tanstack/react-query';
import React from 'react';
import { atom, useRecoilState } from 'recoil';

import * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { useLoadingState, useStateWithDeps } from '@gitbook/util-react-hooks';
import { assert } from '@gitbook/util-ts';

import {
    apiClient,
    apiQuery,
    apiResource,
    fetchAPICache,
    useCurrentOrganization,
} from '../../../state';

/**
 * Atom storing the state persisted in the local storage.
 */
const glossaryPersistedStateAtom = atom<{
    /**
     * Languages to display in the glossary view.
     */
    displayedLanguages: api.TranslationLanguage[];
    /**
     * Changes to be applied to the glossary.
     */
    changes: Array<{
        entryId: GlossaryStateEntryId;
        change: api.GlossaryBatchOperation;
    }>;
}>({
    key: 'glossary:languagesToDisplay',
    default: {
        displayedLanguages: [
            api.TranslationLanguage.En,
            api.TranslationLanguage.Fr,
            api.TranslationLanguage.Es,
            api.TranslationLanguage.Zh,
        ],
        changes: [],
    },
});

export enum GlossaryStateStatus {
    EDITABLE = 'editable',
    SAVING = 'saving',
    FETCHING = 'fetching',
}

export type GlossaryStateActions = {
    fetchMoreEntries: (() => Promise<void>) | undefined;
    setDisplayedLanguages: (languages: api.TranslationLanguage[], extend?: boolean) => void;
    insertGlossaryEntries: (translations: api.GlossaryTranslations[]) => void;
    updateGlossaryEntry: (
        entryId: GlossaryStateEntryId,
        translations: api.GlossaryTranslations
    ) => void;
    deleteGlossaryEntry: (entryId: GlossaryStateEntryId) => void;
    saveChanges: () => Promise<void>;
    discardChanges: () => void;
};

export type GlossaryStateEntryId = number | string;

export type GlossaryStateEntry = {
    /** Unique identifier for the entry. */
    entryId: GlossaryStateEntryId;
    /** The translations for the entry. */
    translations: api.GlossaryTranslations;
    /** The potential saved entry */
    savedEntry: api.GlossaryEntry | null;
};

/**
 * Return the local state for the glossary view.
 */
export function useGlossaryViewState(organizationId: string) {
    const organization = useCurrentOrganization();
    const {
        data: { items: initialFetchedEntries, next: initialFetchedNext },
    } = useSuspenseQuery(
        apiQuery('listGlossaryEntries', {
            organizationId,
            limit: 100,
        })
    );

    const editable = organization.permissions.createContent;

    const [glossaryState, setGlossaryState] = useRecoilState(glossaryPersistedStateAtom);
    const [saving, wrapSaving] = useLoadingState();
    const [fetching, wrapFetching] = useLoadingState();
    const [fetchedNextPage, setFetchedNextPage] = React.useState<string | null>(
        initialFetchedNext?.page ?? null
    );
    const [fetchedEntries, setFetchedEntries] = useStateWithDeps(initialFetchedEntries, [
        initialFetchedEntries,
    ]);

    //
    // Reset the state when the initial fetched entries change.
    //
    React.useEffect(() => {
        if (initialFetchedEntries.length === 0 && editable) {
            // Initialize the state with some dummy entries if none are present.
            setGlossaryState((prev) => ({
                ...prev,
                changes: [
                    ...prev.changes,
                    { entryId: 0, change: { type: 'insert', translations: {} } },
                ],
            }));
        } else {
            setGlossaryState((prev) => ({
                ...prev,
                changes: [],
            }));
        }
    }, [setGlossaryState, initialFetchedEntries, editable]);

    //
    // Fetch the next page of entries.
    //
    const fetchEntries = React.useCallback(async () => {
        return wrapFetching(async () => {
            if (fetchedNextPage === null) {
                return;
            }

            const data = await fetchAPICache(
                apiResource('listGlossaryEntries', {
                    organizationId,
                    page: fetchedNextPage,
                    limit: 100,
                })
            );

            setFetchedEntries((prev) => [...prev, ...data.items]);
            setFetchedNextPage(data.next?.page ?? null);
        });
    }, [organizationId, fetchedNextPage, wrapFetching, setFetchedEntries]);
    const fetchMoreEntries = fetchedNextPage === null ? undefined : fetchEntries;

    //
    // Compute the list of entries to display.
    //
    const entries = React.useMemo(() => {
        const insertedEntries: GlossaryStateEntry[] = [];
        const changesById = new Map<string, api.GlossaryBatchOperation>();
        for (const change of glossaryState.changes) {
            if (change.change.type === 'insert') {
                insertedEntries.push({
                    entryId: change.entryId,
                    translations: change.change.translations,
                    savedEntry: null,
                });
            } else {
                changesById.set(change.change.id, change.change);
            }
        }

        const existingEntries: GlossaryStateEntry[] = [];
        for (const entry of fetchedEntries) {
            const change = changesById.get(entry.id);
            if (change?.type === 'update') {
                existingEntries.push({
                    entryId: entry.id,
                    translations: change.translations,
                    savedEntry: entry,
                });
            } else if (change?.type !== 'delete') {
                existingEntries.push({
                    entryId: entry.id,
                    translations: entry.translations,
                    savedEntry: entry,
                });
            }
        }

        return [...existingEntries, ...insertedEntries];
    }, [fetchedEntries, glossaryState.changes]);

    //
    // Compute the list of changes that can be saved.
    //
    const changesToSave = React.useMemo(() => {
        return glossaryState.changes
            .filter((change) => {
                if (change.change.type !== 'delete') {
                    return !isTranslationsEmpty(change.change.translations);
                }

                return true;
            })
            .map(({ change }) => change);
    }, [glossaryState.changes]);

    const setDisplayedLanguages = React.useCallback(
        (languages: api.TranslationLanguage[], extend = false) => {
            setGlossaryState((prev) => ({
                ...prev,
                displayedLanguages: extend
                    ? [
                          ...prev.displayedLanguages,
                          ...languages.filter((lang) => !prev.displayedLanguages.includes(lang)),
                      ]
                    : languages,
            }));
        },
        [setGlossaryState]
    );

    const insertGlossaryEntries = React.useCallback(
        (entries: api.GlossaryTranslations[]) => {
            assert(editable, 'It should not be called if the user cannot edit');
            setGlossaryState((prev) => {
                const entryId = prev.changes.length;
                return {
                    ...prev,
                    changes: [
                        ...prev.changes,
                        ...entries.map((translations, index) => ({
                            entryId: entryId + index,
                            change: { type: 'insert' as const, translations },
                        })),
                    ],
                };
            });
        },
        [setGlossaryState, editable]
    );

    const updateGlossaryEntry = React.useCallback(
        (entryId: GlossaryStateEntryId, translations: api.GlossaryTranslations) => {
            assert(editable, 'It should not be called if the user cannot edit');
            setGlossaryState((prev) => {
                const foundIndex = prev.changes.findIndex((changeItem) =>
                    typeof entryId === 'string'
                        ? changeItem.change.type === 'update' && changeItem.change.id === entryId
                        : changeItem.entryId === entryId
                );
                const found = foundIndex >= 0 ? prev.changes[foundIndex] : null;

                if (found?.change.type === 'update') {
                    return {
                        ...prev,
                        changes: [
                            ...prev.changes.slice(0, foundIndex),
                            {
                                ...found,
                                change: {
                                    ...found.change,
                                    type: 'update',
                                    translations,
                                },
                            },
                            ...prev.changes.slice(foundIndex + 1),
                        ],
                    };
                }

                if (found?.change.type === 'insert') {
                    return {
                        ...prev,
                        changes: [
                            ...prev.changes.slice(0, foundIndex),
                            {
                                ...found,
                                change: {
                                    ...found.change,
                                    translations,
                                },
                            },
                            ...prev.changes.slice(foundIndex + 1),
                        ],
                    };
                }

                if (!found && typeof entryId === 'string') {
                    return {
                        ...prev,
                        changes: [
                            ...prev.changes,
                            {
                                entryId,
                                change: {
                                    type: 'update',
                                    id: entryId,
                                    translations,
                                },
                            },
                        ],
                    };
                }

                return prev;
            });
        },
        [setGlossaryState, editable]
    );

    const deleteGlossaryEntry = React.useCallback(
        (entryId: GlossaryStateEntryId) => {
            assert(editable, 'It should not be called if the user cannot edit');
            setGlossaryState((prev) => {
                if (typeof entryId === 'number') {
                    // The entry was not saved yet, so we can just remove it from the changes.
                    return {
                        ...prev,
                        changes: prev.changes.filter((c) => c.entryId !== entryId),
                    };
                }

                // The entry was saved, so we need to delete it from the backend.
                return {
                    ...prev,
                    changes: [
                        // Remove any update change
                        ...prev.changes.filter(
                            (c) => c.change.type !== 'update' || c.change.id !== entryId
                        ),
                        // Add a delete change
                        {
                            entryId,
                            change: { type: 'delete', id: entryId },
                        },
                    ],
                };
            });
        },
        [setGlossaryState, editable]
    );

    const saveChanges = React.useCallback(async () => {
        assert(editable, 'It should not be called if the user cannot edit');
        return wrapSaving(async () => {
            if (changesToSave.length > 0) {
                await apiClient.glossary.updateGlossaryEntries(organizationId, {
                    operations: changesToSave,
                });

                const inserts = changesToSave.filter((c) => c.type === 'insert').length;
                const updates = changesToSave.filter((c) => c.type === 'update').length;
                const deletes = changesToSave.filter((c) => c.type === 'delete').length;

                analytics.logEvent({
                    type: 'translation_glossary_edit',
                    organization: organizationId,
                    inserts,
                    updates,
                    deletes,
                });
            }
        });
    }, [wrapSaving, changesToSave, organizationId, editable]);

    const discardChanges = React.useCallback(() => {
        assert(editable, 'It should not be called if the user cannot edit');
        setGlossaryState((prev) => ({
            ...prev,
            changes: [],
        }));
    }, [setGlossaryState, editable]);

    const actions: GlossaryStateActions = {
        fetchMoreEntries,
        setDisplayedLanguages,
        insertGlossaryEntries,
        updateGlossaryEntry,
        deleteGlossaryEntry,
        saveChanges,
        discardChanges,
    };

    return {
        editable,
        displayedLanguages: glossaryState.displayedLanguages,
        hasChanges: changesToSave.length > 0,
        actions,
        saving,
        fetching,
        entries,
    };
}

function isTranslationsEmpty(translations: api.GlossaryTranslations) {
    const values = Object.values(translations);
    return values.length === 0 || values.every((value) => value.trim() === '');
}
